import { useCallback, useMemo, useState } from 'react';
import { Button } from '@ds';
import { MdOutlineHighlightOff } from 'react-icons/md';
import { ChatFilterArrayKeys, ChatFilterState } from '@features/components/Chat/types';
import { prettify } from './utils';

type AppliedFiltersBarProps = {
  draftFilters: ChatFilterState;
  setAndApplyFilter: <K extends keyof ChatFilterState>(key: K, value: ChatFilterState[K]) => void;
};

type ChipData = {
  key: string;
  label: string;
  onRemove: () => void;
};

export const AppliedFiltersBar = ({ draftFilters, setAndApplyFilter }: AppliedFiltersBarProps) => {
  const [showAll, setShowAll] = useState(false);
  const MAX_INLINE = 3;

  const handleRemove = useCallback(
    <K extends ChatFilterArrayKeys>(key: K, value: ChatFilterState[K][number]) => {
      const items = draftFilters[key];
      const newValue = items.filter((x) => x !== value) as ChatFilterState[K];
      setAndApplyFilter(key, newValue);
    },
    [draftFilters, setAndApplyFilter],
  );

  const chips: ChipData[] = useMemo(() => {
    return [
      ...draftFilters.organTypes.map((o) => ({
        key: `organ-${o}`,
        label: prettify(o),
        onRemove: () => handleRemove(ChatFilterArrayKeys.OrganTypes, o),
      })),
      ...draftFilters.donorTypes.map((d) => ({
        key: `donor-${d}`,
        label: prettify(d),
        onRemove: () => handleRemove(ChatFilterArrayKeys.DonorTypes, d),
      })),
      ...draftFilters.chatTypes.map((c) => ({
        key: `chat-${c}`,
        label: prettify(c),
        onRemove: () => handleRemove(ChatFilterArrayKeys.ChatTypes, c),
      })),
    ];
  }, [draftFilters, handleRemove]);

  const visibleChips = useMemo(() => {
    return showAll ? chips : chips.slice(0, MAX_INLINE);
  }, [showAll, chips]);

  const hiddenCount = chips.length - visibleChips.length;

  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-1 mt-1">
      {visibleChips.map(({ key, label, onRemove }) => (
        <span key={key} className="flex items-center gap-2 bg-base-60 px-2 h-7 rounded-full text-m-r text-base-5">
          {label}
          <button
            onClick={onRemove}
            className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-base-80"
            aria-label={`Remove ${label}`}
          >
            <MdOutlineHighlightOff size={15} />
          </button>
        </span>
      ))}

      {hiddenCount > 0 && !showAll && (
        <Button variant="light" size="sm" className="h-7 cursor-pointer" onPress={() => setShowAll(true)}>
          +{hiddenCount}
        </Button>
      )}

      {showAll && chips.length > MAX_INLINE && (
        <Button variant="light" size="sm" className="h-7 underline cursor-pointer" onPress={() => setShowAll(false)}>
          Hide
        </Button>
      )}
    </div>
  );
};
